import { IoImageOutline, IoListOutline, IoTrashOutline } from "react-icons/io5";
import { deleteService, fetchServices } from "../../redux/services";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";

import DataTable from "react-data-table-component";
import { ErrorBanner } from "../../components/banner";
import { Link } from "react-router-dom";
import { Loading } from "../../components/loader";
import { Section } from "../../components/section";
import { ServicesSummaryChart } from "../../components/charts";
import { shadow } from "../../assets/styles/parameters";
import { useEffect } from "react";

export const ServicesList = () => {
  const { loading, services, error } = useSelector((state) => state.services);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  // Handle the delete action natively
  const handleDelete = (id, title) => {
    if (
      window.confirm(`Are you sure you want to delete the service: ${title}?`)
    ) {
      dispatch(deleteService(id));
    }
  };
  const columns = [
    {
      name: "Title",
      selector: (row) => (
        <Link
          to={`edit/${row.id}/${row.title}`}
          state={row}
          style={{ fontWeight: 600, color: "#3B5BDB", textDecoration: "none" }}
        >
          {row.title}
        </Link>
      ),
      sortable: true,
      reorder: true,
    },
    {
      name: "Media Type",
      selector: (row) => {
        const cType = row.media?.[0]?.contentType || "";
        if (cType.startsWith("image/")) return "IMAGE";
        if (cType.startsWith("video/")) return "VIDEO";
        if (cType) return "FILE";
        return "TEXT ONLY";
      },
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionIcon onClick={() => handleDelete(row.id, row.title)}>
          <IoTrashOutline size={18} />
        </ActionIcon>
      ),
      allowOverflow: true,
      button: true,
      width: "80px",
    },
  ];

  if (loading && services.length === 0) return <Loading />;

  return (
    <Section>
      {error && <ErrorBanner text={error} />}

      {/* 1. TOP QUICK STATS */}
      <StatsGrid>
        <StatCard delay="0.1s">
          <StatHeader>
            <IconWrapper bg="#E6F2FF" color="#3B5BDB">
              <IoListOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{services.length}</StatValue>
          <StatLabel>Total Services</StatLabel>
        </StatCard>

        <StatCard delay="0.2s">
          <StatHeader>
            <IconWrapper bg="#F4ECF7" color="#9B59B6">
              <IoImageOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>
            {
              services.filter((s) =>
                s.media?.[0]?.contentType?.startsWith("image/"),
              ).length
            }
          </StatValue>
          <StatLabel>Image Based</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* 2. MAIN LAYOUT: Chart on Left, Table on Right */}
      <WidgetsGrid>
        <WidgetCard delay="0.3s">
          <ServicesSummaryChart services={services} />
        </WidgetCard>

        <WidgetCard delay="0.4s">
          <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#333" }}>
            Service Directory
          </h3>
          <DataTable
            data={services}
            columns={columns}
            striped
            highlightOnHover
            pagination
            responsive
            customStyles={tableStyles}
          />
        </WidgetCard>
      </WidgetsGrid>
    </Section>
  );
};

// --- ANIMATIONS & STYLED COMPONENTS ---

const slideUpFade = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
`;

const StatHeader = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background-color: ${(props) => props.bg};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const StatValue = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin: 0 0 4px 0;
`;

const StatLabel = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const WidgetsGrid = styled.div`
  display: grid;
  grid-template-columns: 0.35fr 0.65fr; /* Chart takes ~1/3, Table takes ~2/3 */
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr; /* Stack on smaller screens */
  }
`;

const WidgetCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  overflow: hidden; /* Keeps the datatable contained */
`;

const ActionIcon = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  transition: background 0.2s;

  &:hover {
    background: #fdedec;
  }
`;

// Clean up the DataTable default styling slightly to match the modern theme
const tableStyles = {
  headRow: {
    style: { borderBottomColor: "#f0f0f0", fontWeight: 600, color: "#555" },
  },
  rows: {
    style: {
      minHeight: "56px",
      "&:not(:last-of-type)": { borderBottomColor: "#f0f0f0" },
    },
  },
};
